import {Observable, Subject, Subscription} from "rxjs";
import {finalize} from "rxjs/operators";

export type FunctionInvocationParameters = {
  self: any;
  args: any[];
};

/**
 * Decorator to debounce a wrapped function
 * Wrapped function must return an Observable.
 * @param {number} [time] - debounce time, default 1000
 */
export function debounceFunction<T>(time = 1000) {
  return function <T> (target, propertyKey: string, descriptor: PropertyDescriptor) { // eslint-disable-line func-names
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const method: (...args) => Observable<T> = descriptor.value; // warning: not bound
    let input: FunctionInvocationParameters = {self: undefined, args: []};
    let timeout;
    let output: Subject<T> = new Subject<T>();

    descriptor.value = function (...args): Observable<T> { // eslint-disable-line func-names
      input = {self: this, args};
      const subscriptions: Subscription[] = [];

      if (output.closed) {
        output = new Subject<T>();
      }

      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      }

      timeout = setTimeout(
        () => {
          const result: Observable<T> = method.apply(input.self, input.args);

          const subscription = result.subscribe(
            (value) => {
              output.next(value);
            },
            (error) => {
              output.error(error);
            },
            () => {
              output.complete();
            },
          );

          subscriptions.push(subscription);
        },
        time,
      );

      return output
        .asObservable()
        .pipe(
          finalize(() => {
            while (subscriptions.length) {
              subscriptions.pop().unsubscribe();
            }

            output.unsubscribe();
          }),
        );
    };

    return descriptor;
  };
}

export function debounceAsyncFunction <T>(time = 1000) { // eslint-disable-line func-names
  return function <T> (target, propertyKey: string, descriptor: PropertyDescriptor) { // eslint-disable-line func-names
    if (descriptor === undefined) {
      descriptor = Object.getOwnPropertyDescriptor(target, propertyKey);
    }

    const method: (...args) => Promise<T> = descriptor.value; // warning: not bound
    let input: FunctionInvocationParameters = {self: undefined, args: []};
    let timeout;
    let output: Subject<T> = new Subject<T>();

    descriptor.value = function (...args): Promise<T> { // eslint-disable-line func-names
      input = {self: this, args};

      if (output.closed) {
        output = new Subject<T>();
      }

      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      }

      timeout = setTimeout(
        () => {
          const result: Promise<T> = method.apply(input.self, input.args);

          console.info("executing debounced method");
          result.then(
            (value) => {
              console.info("debounced method result");
              output.next(value);
              output.complete();
            },
            (error) => {
              console.info("debounced method error");
              output.error(error);
              output.complete();
            },
          );
        },
        time,
      );

      return output.asObservable().toPromise();
    };

    return descriptor;
  };
}
